import { Injectable, computed, signal } from '@angular/core';

/**
 * Stores an OFFSET in milliseconds from real time, not an absolute instant -- that is
 * what makes a simulated clock keep running (see below). Renamed from the absolute-instant
 * key it replaced so a stale value from the old shape cannot be read as an offset.
 */
const STORAGE_KEY = 'absSimulatedTimeOffset';

/** How often the clock advances. Minute-resolution UI, so 30s is ample. */
const TICK_MS = 30_000;

/**
 * The application's idea of "now".
 *
 * Anything that renders differently depending on the time of day should read from
 * here rather than calling Date.now() directly, so an administrator can wind the
 * clock to an event that has not happened yet and see exactly what a player will.
 *
 * Client-side only, by design: this changes what a page *draws*, nothing about what
 * the server stores or decides. A server-held override could leak into real users'
 * sessions, which is a far worse failure than a developer forgetting their own clock
 * is wound forward. It persists in localStorage so it survives the reloads that
 * testing involves.
 *
 * A simulated clock KEEPS RUNNING from the moment it is set: it is an offset applied
 * to real time, not a frozen instant. That is what a real user's clock does, so it is
 * what testing should do -- and it means you can watch time-dependent UI (the schedule's
 * now-line) advance on its own rather than having to take on faith that it would.
 */
@Injectable({ providedIn: 'root' })
export class SimulatedTimeService {
    /** Milliseconds to add to real time. 0 means no simulation. */
    private readonly offsetMs = signal<number>(readStoredOffset());
    private readonly tick = signal(Date.now());

    readonly isSimulated = computed(() => this.offsetMs() !== 0);

    /** Current time: real time plus the offset, advancing either way. */
    readonly now = computed(() => new Date(this.tick() + this.offsetMs()));

    /** The simulated instant, or null when running on real time. */
    readonly override = computed(() => (this.isSimulated() ? this.now() : null));

    constructor() {
        setInterval(() => this.tick.set(Date.now()), TICK_MS);
    }

    /** Milliseconds equivalent of now(), for arithmetic against timestamps. */
    nowMs(): number {
        return this.now().getTime();
    }

    /**
     * Wind the clock to `value`. Stored as the gap between that instant and real time,
     * so the simulated clock then advances in step with the real one -- reload an hour
     * later and the simulation is an hour further on too, exactly as a real user's would be.
     */
    setOverride(value: Date | string | null) {
        if (!value) {
            this.clear();
            return;
        }
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date.getTime())) {
            return;
        }
        const now = Date.now();
        this.tick.set(now);
        this.setOffset(date.getTime() - now);
    }

    clear() {
        this.setOffset(0);
    }

    private setOffset(offset: number) {
        this.offsetMs.set(offset);
        try {
            if (offset === 0) {
                localStorage.removeItem(STORAGE_KEY);
            } else {
                localStorage.setItem(STORAGE_KEY, String(offset));
            }
        } catch {
            // Private browsing / storage disabled -- the offset still applies for this
            // session, it just will not survive a reload.
        }
    }
}

function readStoredOffset(): number {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return 0;
        const offset = Number(raw);
        return Number.isFinite(offset) ? offset : 0;
    } catch {
        return 0;
    }
}
